import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  Grid,
  CircularProgress,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as GenerateIcon,
  Edit as EditIcon,
  AutoMode as TemplateIcon
} from '@mui/icons-material';
import { JuntoApiService, Location, Room, Instructor, ClassCategory } from '../services/api';
import { logger } from '../utils/logger';

interface ClassTemplate {
  id: string;
  name: string;
  description?: string;
  class_name: string;
  location_id: string;
  room_id: string;
  instructor_id: string;
  class_category_id: string;
  difficulty_level?: string;
  special_notes?: string;
  recurrence_type: string;
  start_time: string;
  duration_minutes: number;
  days_of_week?: string[];
  day_of_month?: number;
  interval: number;
  start_date: string;
  generate_weeks_ahead: number;
  is_active: boolean;
  auto_generate: boolean;
  created_at: string;
  updated_at: string;
  last_generated?: string;
}

interface GenerationResult {
  template_id: string;
  generated_count: number;
  skipped_count: number;
  error_count: number;
  generated_class_ids: string[];
  errors: string[];
}

const weekDays = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' }
];

const ClassTemplatesPage: React.FC = () => {
  const [templates, setTemplates] = useState<ClassTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState<{
    open: boolean;
    template: ClassTemplate | null;
  }>({ open: false, template: null });
  const [generateDialog, setGenerateDialog] = useState<{
    open: boolean;
    template: ClassTemplate | null;
    result: GenerationResult | null;
    loading: boolean;
  }>({ open: false, template: null, result: null, loading: false });

  // Form data for creating templates
  const [locations, setLocations] = useState<Location[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [categories, setCategories] = useState<ClassCategory[]>([]);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    class_name: '',
    location_id: '',
    room_id: '',
    instructor_id: '',
    class_category_id: '',
    difficulty_level: 'beginner',
    special_notes: '',
    recurrence_type: 'weekly',
    start_time: '09:00',
    duration_minutes: 60,
    days_of_week: [] as string[],
    day_of_month: 1,
    interval: 1,
    start_date: new Date().toISOString().split('T')[0],
    generate_weeks_ahead: 4,
    is_active: true,
    auto_generate: true
  });

  useEffect(() => {
    loadTemplates();
    loadFormData();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await JuntoApiService.getClassTemplates();
      // TODO: Fix type mismatch between API response and local ClassTemplate interface
      setTemplates((data || []) as any);
    } catch (err) {
      setError('Failed to load class templates');
      logger.error('Error loading templates', err as Error);
    } finally {
      setLoading(false);
    }
  };

  const loadFormData = async () => {
    try {
      const [locationsData, roomsData, instructorsData, categoriesData] = await Promise.all([
        JuntoApiService.getLocations(),
        JuntoApiService.getRooms(),
        JuntoApiService.getInstructors(),
        JuntoApiService.getClassCategories()
      ]);
      
      setLocations(locationsData);
      setRooms(roomsData);
      setInstructors(instructorsData);
      setCategories(categoriesData);
    } catch (err) {
      console.error('Error loading form data:', err);
    }
  };

  const handleCreateTemplate = async () => {
    try {
      // Validate required fields
      if (formData.recurrence_type === 'weekly' && formData.days_of_week.length === 0) {
        setError('Please select at least one day of the week for weekly recurrence');
        return;
      }
      
      const templateData = {
        id: `template-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
        ...formData,
        start_time: formData.start_time + ':00', // Convert HH:MM to HH:MM:SS
        start_date: new Date(formData.start_date).toISOString(),
        // Ensure arrays are properly formatted and not empty
        days_of_week: formData.recurrence_type === 'weekly' ? formData.days_of_week : null,
        day_of_month: formData.recurrence_type === 'monthly' ? formData.day_of_month : null
      };

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const result = await JuntoApiService.createClassTemplate(templateData as any);

      setCreateDialog(false);
      loadTemplates();
      
      // Reset form
      setFormData({
        name: '',
        description: '',
        class_name: '',
        location_id: '',
        room_id: '',
        instructor_id: '',
        class_category_id: '',
        difficulty_level: 'beginner',
        special_notes: '',
        recurrence_type: 'weekly',
        start_time: '09:00',
        duration_minutes: 60,
        days_of_week: [],
        day_of_month: 1,
        interval: 1,
        start_date: new Date().toISOString().split('T')[0],
        generate_weeks_ahead: 4,
        is_active: true,
        auto_generate: true
      });
    } catch (err) {
      setError('Failed to create template');
      console.error('Error creating template:', err);
    }
  };

  const handleEditTemplate = (template: ClassTemplate) => {
    // Populate form with existing template data
    setFormData({
      name: template.name,
      description: template.description || '',
      class_name: template.class_name,
      location_id: template.location_id,
      room_id: template.room_id,
      instructor_id: template.instructor_id,
      class_category_id: template.class_category_id,
      difficulty_level: template.difficulty_level || 'beginner',
      special_notes: template.special_notes || '',
      recurrence_type: template.recurrence_type,
      start_time: template.start_time.substring(0, 5), // Convert HH:MM:SS to HH:MM
      duration_minutes: template.duration_minutes,
      days_of_week: template.days_of_week || [],
      day_of_month: template.day_of_month || 1,
      interval: template.interval,
      start_date: template.start_date.split('T')[0], // Convert ISO to YYYY-MM-DD
      generate_weeks_ahead: template.generate_weeks_ahead,
      is_active: template.is_active,
      auto_generate: template.auto_generate
    });
    setEditDialog({ open: true, template });
  };

  const handleUpdateTemplate = async () => {
    if (!editDialog.template) return;
    
    try {
      // Validate required fields
      if (formData.recurrence_type === 'weekly' && formData.days_of_week.length === 0) {
        setError('Please select at least one day of the week for weekly recurrence');
        return;
      }
      
      const templateData = {
        ...formData,
        start_time: formData.start_time + ':00', // Convert HH:MM to HH:MM:SS
        start_date: new Date(formData.start_date).toISOString(),
        // Ensure arrays are properly formatted and not empty
        days_of_week: formData.recurrence_type === 'weekly' ? formData.days_of_week : null,
        day_of_month: formData.recurrence_type === 'monthly' ? formData.day_of_month : null
      };

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const result = await JuntoApiService.updateClassTemplate({
        id: editDialog.template.id,
        ...templateData
      } as any);

      setEditDialog({ open: false, template: null });
      loadTemplates(); // Reload the templates list
      
      // Reset form
      setFormData({
        name: '',
        description: '',
        class_name: '',
        location_id: '',
        room_id: '',
        instructor_id: '',
        class_category_id: '',
        difficulty_level: 'beginner',
        special_notes: '',
        recurrence_type: 'weekly',
        start_time: '09:00',
        duration_minutes: 60,
        days_of_week: [],
        day_of_month: 1,
        interval: 1,
        start_date: new Date().toISOString().split('T')[0],
        generate_weeks_ahead: 4,
        is_active: true,
        auto_generate: true
      });
    } catch (err) {
      setError('Failed to update template');
      console.error('Error updating template:', err);
    }
  };

  const handleGenerateClasses = async (template: ClassTemplate) => {
    setGenerateDialog({ open: true, template, result: null, loading: true });
    
    try {
      const result = await JuntoApiService.generateClassesFromTemplate(template.id);
      setGenerateDialog(prev => ({ ...prev, result: { 
        template_id: template.id,
        generated_count: result.length,
        skipped_count: 0,
        error_count: 0,
        generated_class_ids: result.map((c: any) => c.id),
        errors: []
      } as GenerationResult, loading: false }));
    } catch (err) {
      setError('Failed to generate classes');
      setGenerateDialog(prev => ({ ...prev, loading: false }));
      console.error('Error generating classes:', err);
    }
  };

  const getRecurrenceDisplay = (template: ClassTemplate) => {
    switch (template.recurrence_type) {
      case 'daily':
        return `Every ${template.interval} day(s)`;
      case 'weekly':
        return `Weekly on ${template.days_of_week?.join(', ')}`;
      case 'monthly':
        return `Monthly on day ${template.day_of_month}`;
      case 'none':
        return 'One-time';
      default:
        return template.recurrence_type;
    }
  };

  const getLocationName = (locationId: string) => {
    const location = locations.find(l => l.id === locationId);
    return location ? location.name : locationId;
  };

  const getRoomName = (roomId: string) => {
    const room = rooms.find(r => r.id === roomId);
    return room ? room.name : roomId;
  };

  const getInstructorName = (instructorId: string) => {
    const instructor = instructors.find(i => i.id === instructorId);
    return instructor ? instructor.name : instructorId;
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" component="h1">
          Class Templates
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialog(true)}
        >
          Create Template
        </Button>
      </Box>

      <Typography variant="body1" color="textSecondary" mb={3}>
        Create templates to automatically generate recurring classes. Set up your schedule once and let the system handle the rest!
      </Typography>

      {error && (
        <Alert severity="error" style={{ marginBottom: '1rem' }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Template Name</TableCell>
              <TableCell>Class Name</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Instructor</TableCell>
              <TableCell>Recurrence</TableCell>
              <TableCell>Time</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Generated</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.map((template) => (
              <TableRow key={template.id}>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {template.name}
                    </Typography>
                    {template.description && (
                      <Typography variant="caption" color="textSecondary">
                        {template.description}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>{template.class_name}</TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {getLocationName(template.location_id)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {getRoomName(template.room_id)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{getInstructorName(template.instructor_id)}</TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {getRecurrenceDisplay(template)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {template.start_time}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {template.duration_minutes}min
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={template.is_active ? 'Active' : 'Inactive'}
                    color={template.is_active ? 'success' : 'default'}
                    size="small"
                  />
                  {template.auto_generate && (
                    <Chip
                      label="Auto"
                      color="primary"
                      size="small"
                      sx={{ ml: 0.5 }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  {template.last_generated ? (
                    <Typography variant="caption">
                      {new Date(template.last_generated).toLocaleDateString()}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="textSecondary">
                      Never
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip title="Edit Template">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEditTemplate(template)}
                      sx={{ mr: 1 }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Generate Classes">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleGenerateClasses(template)}
                    >
                      <GenerateIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {templates.length === 0 && (
        <Paper elevation={1} style={{ padding: '2rem', textAlign: 'center', marginTop: '2rem' }}>
          <TemplateIcon color="disabled" style={{ fontSize: 48, marginBottom: '1rem' }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No class templates found
          </Typography>
          <Typography variant="body2" color="textSecondary" marginBottom={2}>
            Create your first template to start automatically generating classes!
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialog(true)}
          >
            Create Your First Template
          </Button>
        </Paper>
      )}

      {/* Create Template Dialog */}
      <Dialog
        open={createDialog}
        onClose={() => setCreateDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create Class Template</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Basic Info */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Template Name"
                fullWidth
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Morning Yoga Template"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Class Name"
                fullWidth
                value={formData.class_name}
                onChange={(e) => setFormData({ ...formData, class_name: e.target.value })}
                placeholder="e.g., Morning Flow Yoga"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description for this template"
              />
            </Grid>

            {/* Location & Space */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Location</InputLabel>
                <Select
                  value={formData.location_id}
                  onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
                >
                  {locations.map((location) => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Room</InputLabel>
                <Select
                  value={formData.room_id}
                  onChange={(e) => setFormData({ ...formData, room_id: e.target.value })}
                >
                  {rooms.map((room) => (
                    <MenuItem key={room.id} value={room.id}>
                      {room.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Instructor & Category */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Instructor</InputLabel>
                <Select
                  value={formData.instructor_id}
                  onChange={(e) => setFormData({ ...formData, instructor_id: e.target.value })}
                >
                  {instructors.map((instructor) => (
                    <MenuItem key={instructor.id} value={instructor.id}>
                      {instructor.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Class Category</InputLabel>
                <Select
                  value={formData.class_category_id}
                  onChange={(e) => setFormData({ ...formData, class_category_id: e.target.value })}
                >
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Class Details */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Duration (minutes)"
                type="number"
                fullWidth
                value={formData.duration_minutes}
                onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Difficulty Level</InputLabel>
                <Select
                  value={formData.difficulty_level}
                  onChange={(e) => setFormData({ ...formData, difficulty_level: e.target.value })}
                  required
                >
                  <MenuItem value="beginner">Beginner</MenuItem>
                  <MenuItem value="intermediate">Intermediate</MenuItem>
                  <MenuItem value="advanced">Advanced</MenuItem>
                  <MenuItem value="beginner">Beginner</MenuItem>
                  <MenuItem value="intermediate">Intermediate</MenuItem>
                  <MenuItem value="advanced">Advanced</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Special Notes"
                fullWidth
                multiline
                rows={2}
                value={formData.special_notes}
                onChange={(e) => setFormData({ ...formData, special_notes: e.target.value })}
                placeholder="Any special instructions or notes for generated classes"
              />
            </Grid>

            {/* Scheduling */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Recurrence Type</InputLabel>
                <Select
                  value={formData.recurrence_type}
                  onChange={(e) => setFormData({ ...formData, recurrence_type: e.target.value })}
                >
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="none">One-time</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Start Time"
                type="time"
                fullWidth
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Days of Week (for weekly recurrence) */}
            {formData.recurrence_type === 'weekly' && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Days of Week
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {weekDays.map((day) => (
                    <FormControlLabel
                      key={day.value}
                      control={
                        <Checkbox
                          checked={formData.days_of_week.includes(day.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                days_of_week: [...formData.days_of_week, day.value]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                days_of_week: formData.days_of_week.filter(d => d !== day.value)
                              });
                            }
                          }}
                        />
                      }
                      label={day.label}
                    />
                  ))}
                </Box>
              </Grid>
            )}

            {/* Day of Month (for monthly recurrence) */}
            {formData.recurrence_type === 'monthly' && (
              <Grid item xs={12} md={6}>
                <TextField
                  label="Day of Month"
                  type="number"
                  fullWidth
                  value={formData.day_of_month}
                  onChange={(e) => setFormData({ ...formData, day_of_month: parseInt(e.target.value) })}
                  inputProps={{ min: 1, max: 31 }}
                />
              </Grid>
            )}

            {/* Dates */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Generation Settings */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Generate Weeks Ahead"
                type="number"
                fullWidth
                value={formData.generate_weeks_ahead}
                onChange={(e) => setFormData({ ...formData, generate_weeks_ahead: parseInt(e.target.value) })}
                inputProps={{ min: 1, max: 52 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box mt={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                  }
                  label="Active Template"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.auto_generate}
                      onChange={(e) => setFormData({ ...formData, auto_generate: e.target.checked })}
                    />
                  }
                  label="Auto Generate"
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateTemplate} 
            variant="contained" 
            color="primary"
            disabled={!formData.name || !formData.class_name || !formData.location_id || !formData.room_id || !formData.instructor_id || !formData.class_category_id}
          >
            Create Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Template Dialog */}
      <Dialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, template: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Class Template</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Basic Info */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Template Name"
                fullWidth
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Morning Yoga Template"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Class Name"
                fullWidth
                value={formData.class_name}
                onChange={(e) => setFormData({ ...formData, class_name: e.target.value })}
                placeholder="e.g., Morning Flow Yoga"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description for this template"
              />
            </Grid>

            {/* Location & Space */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Location</InputLabel>
                <Select
                  value={formData.location_id}
                  onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
                >
                  {locations.map((location) => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Room</InputLabel>
                <Select
                  value={formData.room_id}
                  onChange={(e) => setFormData({ ...formData, room_id: e.target.value })}
                >
                  {rooms.map((room) => (
                    <MenuItem key={room.id} value={room.id}>
                      {room.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Instructor & Category */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Instructor</InputLabel>
                <Select
                  value={formData.instructor_id}
                  onChange={(e) => setFormData({ ...formData, instructor_id: e.target.value })}
                >
                  {instructors.map((instructor) => (
                    <MenuItem key={instructor.id} value={instructor.id}>
                      {instructor.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Class Category</InputLabel>
                <Select
                  value={formData.class_category_id}
                  onChange={(e) => setFormData({ ...formData, class_category_id: e.target.value })}
                >
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Class Details */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Duration (minutes)"
                type="number"
                fullWidth
                value={formData.duration_minutes}
                onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Difficulty Level</InputLabel>
                <Select
                  value={formData.difficulty_level}
                  onChange={(e) => setFormData({ ...formData, difficulty_level: e.target.value })}
                  required
                >
                  <MenuItem value="beginner">Beginner</MenuItem>
                  <MenuItem value="intermediate">Intermediate</MenuItem>
                  <MenuItem value="advanced">Advanced</MenuItem>
                  <MenuItem value="beginner">Beginner</MenuItem>
                  <MenuItem value="intermediate">Intermediate</MenuItem>
                  <MenuItem value="advanced">Advanced</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Special Notes"
                fullWidth
                multiline
                rows={2}
                value={formData.special_notes}
                onChange={(e) => setFormData({ ...formData, special_notes: e.target.value })}
                placeholder="Any special instructions or notes for generated classes"
              />
            </Grid>

            {/* Scheduling */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Recurrence Type</InputLabel>
                <Select
                  value={formData.recurrence_type}
                  onChange={(e) => setFormData({ ...formData, recurrence_type: e.target.value })}
                >
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="none">One-time</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Start Time"
                type="time"
                fullWidth
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Days of Week (for weekly recurrence) */}
            {formData.recurrence_type === 'weekly' && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Days of Week
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {weekDays.map((day) => (
                    <FormControlLabel
                      key={day.value}
                      control={
                        <Checkbox
                          checked={formData.days_of_week.includes(day.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                days_of_week: [...formData.days_of_week, day.value]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                days_of_week: formData.days_of_week.filter(d => d !== day.value)
                              });
                            }
                          }}
                        />
                      }
                      label={day.label}
                    />
                  ))}
                </Box>
              </Grid>
            )}

            {/* Day of Month (for monthly recurrence) */}
            {formData.recurrence_type === 'monthly' && (
              <Grid item xs={12} md={6}>
                <TextField
                  label="Day of Month"
                  type="number"
                  fullWidth
                  value={formData.day_of_month}
                  onChange={(e) => setFormData({ ...formData, day_of_month: parseInt(e.target.value) })}
                  inputProps={{ min: 1, max: 31 }}
                />
              </Grid>
            )}

            {/* Dates */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Generation Settings */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Generate Weeks Ahead"
                type="number"
                fullWidth
                value={formData.generate_weeks_ahead}
                onChange={(e) => setFormData({ ...formData, generate_weeks_ahead: parseInt(e.target.value) })}
                inputProps={{ min: 1, max: 52 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box mt={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                  }
                  label="Active Template"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.auto_generate}
                      onChange={(e) => setFormData({ ...formData, auto_generate: e.target.checked })}
                    />
                  }
                  label="Auto Generate"
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, template: null })}>Cancel</Button>
          <Button 
            onClick={handleUpdateTemplate} 
            variant="contained" 
            color="primary"
            disabled={!formData.name || !formData.class_name || !formData.location_id || !formData.room_id || !formData.instructor_id || !formData.class_category_id}
          >
            Update Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generation Result Dialog */}
      <Dialog
        open={generateDialog.open}
        onClose={() => setGenerateDialog({ open: false, template: null, result: null, loading: false })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Generate Classes: {generateDialog.template?.name}
        </DialogTitle>
        <DialogContent>
          {generateDialog.loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
              <Typography variant="body1" ml={2}>
                Generating classes...
              </Typography>
            </Box>
          ) : generateDialog.result ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Class generation completed!
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="h6" color="success.main">
                    {generateDialog.result.generated_count}
                  </Typography>
                  <Typography variant="body2">Classes Generated</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h6" color="warning.main">
                    {generateDialog.result.skipped_count}
                  </Typography>
                  <Typography variant="body2">Classes Skipped</Typography>
                </Grid>
                {generateDialog.result.error_count > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="h6" color="error.main">
                      {generateDialog.result.error_count} Errors
                    </Typography>
                    {generateDialog.result.errors.map((error, index) => (
                      <Typography key={index} variant="body2" color="error">
                        • {error}
                      </Typography>
                    ))}
                  </Grid>
                )}
              </Grid>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setGenerateDialog({ open: false, template: null, result: null, loading: false })}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ClassTemplatesPage;